// Shared badge category + asset definitions used across the app.
// Badge asset naming scheme: [restroomcategory]_[BadgeName]_[BadgeColor]_100.png

export const BADGE_CATEGORIES = [
  {
    id: 'bars_nightclubs',
    label: 'Bars / Nightclubs',
    tiers: { bronze: 'happy_hour', silver: 'buzzed_bathroomer', gold: 'taproom_titan' }
  },
  {
    id: 'transit',
    label: 'Transit',
    tiers: { bronze: 'transit_tummy', silver: 'terminal_titan', gold: 'world_wiper' }
  },
  {
    id: 'restaurant',
    label: 'Restaurants',
    tiers: { bronze: 'food_flusher', silver: 'fork_and_flush', gold: 'michelin_throne' }
  },
  {
    id: 'stadium',
    label: 'Stadiums',
    tiers: { bronze: 'game_time', silver: 'pro_goer', gold: 'most_valuable_porcelain' }
  },
  {
    id: 'office',
    label: 'Office',
    tiers: { bronze: 'working_overtime', silver: 'office_insider', gold: 'bathroom_boss' }
  },
  {
    id: 'coffee_shop',
    label: 'Coffee Shops',
    tiers: { bronze: 'bean_boss', silver: 'coffee_connoisseur', gold: 'latte_legend' }
  },
  {
    id: 'park',
    label: 'Parks',
    tiers: { bronze: 'forest_flush', silver: 'porcelain_ranger', gold: 'natures_throne' }
  },
  {
    id: 'retail',
    label: 'Retail',
    tiers: { bronze: 'retail_relief', silver: 'mall_marker', gold: 'shopping_savior' }
  },
  {
    id: 'gas_station',
    label: 'Gas Stations',
    tiers: { bronze: 'dump_and_run', silver: 'highway_hero', gold: 'fueling_the_flush' }
  },
  {
    id: 'truck_stop',
    label: 'Truck Stops',
    tiers: { bronze: 'road_relief', silver: 'diesel_defender', gold: 'truck_stop_king' }
  },
  {
    id: 'fast_food',
    label: 'Fast Food',
    assetId: 'fastfood', // asset files use `fastfood_` prefix (no underscore)
    tiers: { bronze: 'quick_relief', silver: 'meal_feeder', gold: 'fast_food_king' }
  },
  {
    id: 'grocery_store',
    label: 'Grocery Store',
    tiers: { bronze: 'aisle_relief', silver: 'pantry_patron', gold: 'grocery_goat' }
  },
  {
    id: 'public_facility',
    label: 'Public Facility',
    tiers: { bronze: 'public_provider', silver: 'community_champ', gold: 'public_presenter' }
  },
  {
    id: 'hotel_motel',
    label: 'Hotel/Motel',
    tiers: { bronze: 'roomie', silver: 'hotel_savior', gold: 'hospitality_hero' }
  },
  {
    id: 'other',
    label: 'Other',
    tiers: { bronze: 'mystery_relief', silver: 'unsung', gold: 'beyond_and_beyond' }
  }
];

export const TIER_ORDER = ['bronze', 'silver', 'gold'];
export const TIER_REQUIREMENT = { bronze: 1, silver: 10, gold: 25 };

// Display colors per tier (match the profile legend + tier pills).
export const TIER_COLORS = {
  bronze: '#c56a2e',
  silver: '#a9a9a9',
  gold: '#faaf40'
};

const TIER_FOR_THRESHOLD = { 1: 'bronze', 10: 'silver', 25: 'gold' };

// New generic SVG assets: public/assets/badges/{Prefix}{Variant}.svg where
// Variant is Bronze/Silver/Gold/Lock/Award. File names have a few one-off
// casings/typos on disk (case-sensitive in production Linux), so resolve
// through this table instead of string-building.
const BADGE_FILE_PREFIX = {
  bars_nightclubs: 'Bars',
  transit: 'Transport',
  restaurant: 'Restaurant',
  stadium: 'Stadium',
  office: 'Office',
  coffee_shop: 'Coffee',
  park: 'Park',
  retail: 'Retail',
  gas_station: 'GasStation',
  truck_stop: 'Truck',
  fast_food: 'FastFood',
  grocery_store: 'Grocery',
  public_facility: 'Public',
  hotel_motel: 'Hotel',
  other: 'Other'
};

// Exact on-disk file names that differ from the {Prefix}{Variant} pattern.
const BADGE_FILE_OVERRIDES = {
  'Truck:gold': 'Truckgold.svg',
  'Public:gold': 'Publicgold.svg',
  'Public:silver': 'Publicsilver.svg',
  'Stadium:award': 'StadumAward.svg'
};

// TemplateLock.svg remains the generic fallback for unknown categories.
const BADGE_LOCK_FALLBACK = 'TemplateLock.svg';

const VARIANTS = ['bronze', 'silver', 'gold', 'lock', 'award'];

function getBadgeFileName(categoryId, variant) {
  const normalized = VARIANTS.includes(variant) ? variant : 'lock';
  const prefix = BADGE_FILE_PREFIX[categoryId];
  if (!prefix) return BADGE_LOCK_FALLBACK;
  const overrideKey = `${prefix}:${normalized}`;
  if (BADGE_FILE_OVERRIDES[overrideKey]) {
    return BADGE_FILE_OVERRIDES[overrideKey];
  }
  return `${prefix}${normalized[0].toUpperCase()}${normalized.slice(1)}.svg`;
}

function resolveCategoryId(category) {
  if (!category) return null;
  return typeof category === 'string' ? category : category.id;
}

export function getBadgeImagePath(category, tier) {
  return `/assets/badges/${getBadgeFileName(resolveCategoryId(category), tier)}`;
}

export function getBadgeLockImagePath(category) {
  return `/assets/badges/${getBadgeFileName(resolveCategoryId(category), 'lock')}`;
}

export function getBadgeAwardImagePath(category) {
  return `/assets/badges/${getBadgeFileName(resolveCategoryId(category), 'award')}`;
}

// Resolve a badge id (e.g. `gas_station_10`) to its category/tier info and
// asset path. Returns null for unknown ids (e.g. removed tracks like
// frequent_flusher).
export function getBadgeInfoFromId(badgeId) {
  if (!badgeId || typeof badgeId !== 'string') return null;
  const match = badgeId.match(/^(.*)_(1|10|25)$/);
  if (!match) return null;
  const [, categoryId, thresholdStr] = match;
  const threshold = Number(thresholdStr);
  const tier = TIER_FOR_THRESHOLD[threshold];
  if (!tier) return null;

  const category = BADGE_CATEGORIES.find((c) => c.id === categoryId);
  if (!category) return null;

  return {
    category,
    threshold,
    tier,
    imagePath: getBadgeImagePath(category, tier)
  };
}
